import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

/**
 * Global black-glass confirmation dialog.
 *
 * One reusable confirm surface for high-intent actions across the app —
 * accessing an account, exiting an access session, deleting content, etc.
 * It is deliberately theme-independent (always dark glass) so it looks the
 * same everywhere it's dropped in.
 *
 * Props:
 *  - open, onConfirm, onCancel
 *  - title (string/node), description (string/node, optional)
 *  - confirmLabel, cancelLabel
 *  - loading: shows a spinner on the confirm button and blocks dismiss
 *  - tone: 'default' (blue) | 'danger' (red) — colours the confirm button
 *  - icon: optional node rendered in a tinted badge above the title
 *  - hideCancel: confirm-only (acknowledgement) dialogs
 */
export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  tone = 'default',
  icon = null,
  hideCancel = false,
}) => {
  const danger = tone === 'danger';
  const confirmSx = danger
    ? { bg: '#ef4444', hover: '#dc2626', fg: '#fff' }
    : { bg: '#5aa5ef', hover: '#3f8fe0', fg: '#0b1220' };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'rgba(18,20,28,0.92)',
          backdropFilter: 'blur(22px)',
          WebkitBackdropFilter: 'blur(22px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          color: '#f5f5f7',
          fontFamily: 'Montserrat, sans-serif',
          overflow: 'hidden',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        },
      }}
    >
      <Box sx={{ p: 3, position: 'relative' }}>
        {!loading && !hideCancel && (
          <IconButton
            onClick={onCancel}
            size="small"
            aria-label="Close"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: 'rgba(255,255,255,0.55)',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        )}

        {icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              mb: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: danger ? 'rgba(239,68,68,0.16)' : 'rgba(90,165,239,0.16)',
              color: danger ? '#f87171' : '#5aa5ef',
            }}
          >
            {icon}
          </Box>
        )}

        <Typography sx={{ fontSize: 18, fontWeight: 700, pr: 3, fontFamily: 'inherit', lineHeight: 1.3 }}>
          {title}
        </Typography>
        {description ? (
          <Typography sx={{ mt: 1, fontSize: 14, lineHeight: 1.55, color: 'rgba(245,245,247,0.72)', fontFamily: 'inherit' }}>
            {description}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={1.25} sx={{ mt: 3, justifyContent: 'flex-end' }}>
          {!hideCancel && (
            <Button
              onClick={onCancel}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 2,
                color: 'rgba(245,245,247,0.85)',
                fontFamily: 'inherit',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            onClick={onConfirm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: confirmSx.fg }} /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
              bgcolor: confirmSx.bg,
              color: confirmSx.fg,
              fontFamily: 'inherit',
              boxShadow: 'none',
              '&:hover': { bgcolor: confirmSx.hover, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: confirmSx.bg, opacity: 0.75, color: confirmSx.fg },
            }}
          >
            {loading ? 'Please wait…' : confirmLabel}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default ConfirmDialog;
